import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SeverityBadge } from "@/components/Logo";
import { eur, eurUnit, num, pct, dateFr, severityOf, unitLabel } from "@/lib/format";
import { t } from "@/i18n";

export function ChangeBadge({ value, testId }) {
  if (value == null) return <span className="text-xs text-muted-foreground">{t("invoiceDetailDialog.nouveau")}</span>;
  if (Math.abs(value) < 1) return <span className="text-xs text-muted-foreground">{t("invoiceDetailDialog.stable")}</span>;
  return <SeverityBadge testId={testId} severity={value > 0 ? severityOf(value) : "green"}>{pct(value, true)}</SeverityBadge>;
}

export default function InvoiceDetailDialog({ invoice, onClose }) {
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="invoice-detail-dialog">
        {invoice && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">{invoice.supplier_name}</DialogTitle>
              <DialogDescription>
                {t("invoiceDetailDialog.facture_du")} {dateFr(invoice.invoice_date)} · <span className="num">{eur(invoice.total_amount)}</span> {t("invoiceDetailDialog.ht")} {invoice.lines?.length} {t("common.lines_suffix")}
              </DialogDescription>
            </DialogHeader>
            {invoice.summary && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="invoice-import-summary">
                {[[t("invoiceDetailDialog.nouveaux_ingredients"), invoice.summary.new_ingredients], [t("invoiceDetailDialog.prix_mis_a_jour"), invoice.summary.updated], [t("invoiceDetailDialog.hausses_detectees"), invoice.summary.increases], [t("invoiceDetailDialog.recettes_recalculees"), invoice.summary.recipes_recalculated || 0]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border bg-muted/30 p-3">
                    <p className="num text-xl font-semibold">{v}</p>
                    <p className="text-xs text-muted-foreground">{l}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="min-w-0 overflow-x-auto rounded-lg border">
              <Table data-testid="invoice-lines-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("invoiceDetailDialog.libelle_facture")}</TableHead>
                    <TableHead>{t("common.ingredient")}</TableHead>
                    <TableHead className="text-right">{t("invoiceDetailDialog.qte")}</TableHead>
                    <TableHead className="text-right">{t("invoiceDetailDialog.prix_unit")}</TableHead>
                    <TableHead className="text-right">{t("invoiceDetailDialog.total")}</TableHead>
                    <TableHead className="text-right">{t("invoiceDetailDialog.variation")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.lines?.map((l) => (
                    <TableRow key={l.id} data-testid={`invoice-line-${l.id}`}>
                      <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{l.raw_name}</TableCell>
                      <TableCell className="font-medium">{l.ingredient_name || "—"}</TableCell>
                      <TableCell className="num text-right">{num(l.quantity)} {unitLabel(l.unit)}</TableCell>
                      <TableCell className="num text-right">{eurUnit(l.unit_price)}</TableCell>
                      <TableCell className="num text-right">{eur(l.total_price)}</TableCell>
                      <TableCell className="text-right"><ChangeBadge value={l.price_change_pct} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
